import type { PostHog } from "posthog-js";
import { scheduleAnalyticsAfterLoad } from "./deferred-analytics.client";
import { isAnalyticsMutedForThisLoad } from "./embed-mute";

const MAX_PENDING_OPERATIONS = 100;

type AnalyticsProperties = Record<string, unknown>;
type PendingOperation =
  | {
      type: "capture";
      event: string;
      properties?: AnalyticsProperties;
    }
  | {
      type: "identify";
      userId: string;
      properties?: AnalyticsProperties;
    };

type PostHogGlobal = Pick<PostHog, "capture" | "identify">;

let client: PostHog | null = null;
let loadPromise: Promise<PostHog | null> | null = null;
let initializationScheduled = false;
const pendingOperations: PendingOperation[] = [];
// init 直後・溜まった操作を再生する前の distinct_id。identify が走ると
// distinct_id は user id に入れ替わるので、anonymous 側の名乗り
// (/api/learning-events/claim が受ける anonymousId) はここでしか拾えない
let bootDistinctId: string | null = null;
const readyWaiters: Array<(client: PostHog | null) => void> = [];

function getPostHogConfig() {
  if (typeof window === "undefined") return null;
  // 親のページが計測している埋め込み (/skillmap/demo?embed=1) では動かさない
  if (isAnalyticsMutedForThisLoad()) return null;
  const apiKey = window.ENV?.POSTHOG_API_KEY;
  if (!apiKey) return null;

  return {
    apiKey,
    apiHost: window.ENV?.POSTHOG_API_HOST || "https://app.posthog.com",
  };
}

function runOperation(posthog: PostHog, operation: PendingOperation) {
  try {
    if (operation.type === "capture") {
      posthog.capture(operation.event, operation.properties);
    } else {
      posthog.identify(operation.userId, operation.properties);
    }
  } catch {
    // Analytics is observational and must never interrupt a user action.
  }
}

function enqueue(operation: PendingOperation) {
  if (pendingOperations.length >= MAX_PENDING_OPERATIONS) {
    pendingOperations.shift();
  }
  pendingOperations.push(operation);
}

function setBrowserPostHog(posthog: PostHogGlobal) {
  (window as unknown as { posthog?: PostHogGlobal }).posthog = posthog;
}

function installEarlyEventProxy() {
  const analyticsWindow = window as unknown as { posthog?: PostHogGlobal };
  if (analyticsWindow.posthog) return;

  analyticsWindow.posthog = {
    capture: (event, properties) => {
      capturePostHogEvent(event, properties as AnalyticsProperties | undefined);
      return undefined;
    },
    identify: (userId, properties) => {
      identifyPostHogUser(
        String(userId),
        properties as AnalyticsProperties | undefined,
      );
    },
  } as PostHogGlobal;
}

function flushPendingOperations(posthog: PostHog) {
  const operations = pendingOperations.splice(0, pendingOperations.length);
  for (const operation of operations) {
    runOperation(posthog, operation);
  }
}

async function initializePostHog(): Promise<PostHog | null> {
  if (client) return client;
  if (loadPromise) return loadPromise;

  const config = getPostHogConfig();
  if (!config) return null;

  loadPromise = import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(config.apiKey, {
        api_host: config.apiHost,
        person_profiles: "identified_only",
        capture_pageview: false,
        // Keep replay structure available without exporting form values or
        // page text. Event analytics remain unchanged.
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "*",
        },
        loaded: (loadedClient) => {
          setBrowserPostHog(loadedClient);
          if (process.env.NODE_ENV === "development") {
            console.log("PostHog loaded successfully");
          }
        },
      });

      client = posthog;
      setBrowserPostHog(posthog);
      try {
        bootDistinctId = posthog.get_distinct_id() || null;
      } catch {
        bootDistinctId = null;
      }
      flushPendingOperations(posthog);
      resolveReadyWaiters(posthog);
      return posthog;
    })
    .catch(() => {
      // A blocked analytics origin or extension must not affect the app.
      if (process.env.NODE_ENV === "development") {
        console.warn("PostHog failed to load");
      }
      resolveReadyWaiters(null);
      return null;
    });

  return loadPromise;
}

export function schedulePostHogInitialization() {
  if (
    initializationScheduled ||
    client ||
    typeof window === "undefined" ||
    !getPostHogConfig()
  ) {
    return;
  }

  initializationScheduled = true;
  installEarlyEventProxy();
  scheduleAnalyticsAfterLoad(() => {
    void initializePostHog();
  });
}

export function capturePostHogEvent(
  event: string,
  properties?: AnalyticsProperties,
) {
  if (typeof window === "undefined" || !getPostHogConfig()) return;
  if (client) {
    runOperation(client, { type: "capture", event, properties });
    return;
  }

  enqueue({ type: "capture", event, properties });
  schedulePostHogInitialization();
}

export function capturePostHogPageView(pathname: string) {
  if (typeof window === "undefined") return;
  capturePostHogEvent("$pageview", {
    $current_url: new URL(pathname, window.location.href).href,
  });
}

export function identifyPostHogUser(
  userId: string,
  properties?: AnalyticsProperties,
) {
  if (typeof window === "undefined" || !getPostHogConfig()) return;
  if (client) {
    runOperation(client, { type: "identify", userId, properties });
    return;
  }

  enqueue({ type: "identify", userId, properties });
  schedulePostHogInitialization();
}

function resolveReadyWaiters(loaded: PostHog | null) {
  const waiters = readyWaiters.splice(0, readyWaiters.length);
  for (const resolve of waiters) resolve(loaded);
}

/**
 * 遅れて来る PostHog の準備を待つ。**init を前倒しでは起こさない。**
 * schedulePostHogInitialization が既に並べた load + idle の後に解決する。
 * key 未設定・ブロック・読み込み失敗では null で解決する。
 */
export function whenPostHogClientReady(): Promise<PostHog | null> {
  if (client) return Promise.resolve(client);
  if (loadPromise) return loadPromise;
  if (typeof window === "undefined" || !getPostHogConfig()) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    readyWaiters.push(resolve);
  });
}

export interface PostHogSessionIdentity {
  /**
   * identify が走る直前までブラウザが名乗っていた distinct_id。
   * PostHog 上の匿名イベントと learning_events.anonymous_id を結ぶ鍵
   */
  anonymousId: string;
  /** PostHog の replay/session のまとまり。取れなければ undefined */
  sessionId?: string;
}

/**
 * 認証が確かめられたときに一度だけ呼ぶ。PostHog の遅延 init を待ってから、
 * 「名乗っていた anonymous 側の distinct_id」を読んで identify する。
 *
 * 読む順序が大事。identify() は distinct_id を user id に入れ替えるので、
 * 先に読まないと anonymous 側の id は失われる。既に user id へ
 * 入れ替わっていた場合 (永続化された identify や、先に再生された queued
 * identify) は init 直後に控えた bootDistinctId を名乗る。
 */
export async function identifyPostHogUserForSession(
  userId: string,
): Promise<PostHogSessionIdentity | null> {
  if (typeof window === "undefined" || !getPostHogConfig()) return null;
  schedulePostHogInitialization();
  const posthog = await whenPostHogClientReady();
  if (!posthog) return null;
  try {
    const currentDistinctId = posthog.get_distinct_id();
    const anonymousId =
      currentDistinctId === userId
        ? (bootDistinctId ?? currentDistinctId)
        : currentDistinctId;
    let sessionId: string | undefined;
    try {
      sessionId = posthog.get_session_id() || undefined;
    } catch {
      // session id は replay との結びつきを良くするだけ。取れなくても進める
    }
    runOperation(posthog, { type: "identify", userId });
    return anonymousId ? { anonymousId, sessionId } : null;
  } catch {
    return null;
  }
}

/**
 * ログアウトが確かめられたときに呼ぶ。identify 済みの distinct_id を
 * 持ち越すと、共有PCで別人がログインしたときに前の人の匿名期の記録まで
 * 名乗ってしまう。reset で新しい anonymous id に切り替える。
 *
 * 未ロード時に溜まっている identify があれば掃く。残したままだと
 * ログアウト後の遅延 init で本人として名乗り直す。
 */
export function resetPostHogUser() {
  for (let i = pendingOperations.length - 1; i >= 0; i--) {
    if (pendingOperations[i].type === "identify") pendingOperations.splice(i, 1);
  }
  try {
    client?.reset();
  } catch {
    // Analytics is observational and must never interrupt a user action.
  }
}
